import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaTimes,
  FaPencilAlt,
  FaSave,
  FaStar,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import yaml from 'js-yaml';
import { useAppContext } from '../Store/Store';
import { useNavigate, useLocation } from 'react-router-dom';
import './Management.css';
import EmployeeNavbar from '../components/Extra/EmployeeNavbar';
import AccessLogs from './AccessHistory';
import ProductsTab from './Products';
import MarketTestTab from './MarketTestConfigs';

interface Feedback {
  id: string;
  title: string;
  text: string;
  rating: number;
  created_at: string;
  insights: string;
  sentiment: string;
  tags: [string, string][];
  type: string;
  filename?: string;
  token: string;
  product_id?: number;
  event_id?: number;
  product_name?: string;
  event_name?: string;
  product_image?: string;
}

interface Event {
  id: number;
  name: string;
  code: string;
}

interface Product {
  id: number;
  name: string;
  code: string;
}

interface PaginationInfo {
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface GeoProduct {
  id: number;
  product_name: string;
  serial_number: string;
  registered_location: string;
  registration_date: string;
  event_name?: string;
  event_id?: number;
  code: string;
}

type EmpTab = 'feedbacks' | 'products' | 'market-test' | 'access-logs' | 'geo';

/** Dashboard tab → employee permission required to see it. */
const TAB_PERMISSION: Record<EmpTab, string> = {
  feedbacks: 'Feedbacks',
  products: 'Products',
  'market-test': 'MarketTest',
  'access-logs': 'Geo',
  geo: 'Geo',
};

/** Order used when picking the first tab a user is allowed to see. */
const TAB_ORDER: EmpTab[] = [
  'feedbacks',
  'products',
  'market-test',
  'access-logs',
  'geo',
];

export const Feedbacks: React.FC = () => {
  const { user, isAuthenticated, login, backendBaseUrl, resolveMediaUrl } =
    useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  /** A tab is visible when the user has its permission. No permissions configured
   *  means full access (legacy employees created before the permission system). */
  const canSeeTab = (tab: EmpTab): boolean =>
    (user?.employee_permissions || []).includes(TAB_PERMISSION[tab]);

  const getInitialTab = (): EmpTab => TAB_ORDER.find(canSeeTab) || 'feedbacks';

  const [activeTab, setActiveTab] = useState<EmpTab>('feedbacks');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [editingFeedbackId, setEditingFeedbackId] = useState<string | null>(
    null
  );
  const [editingText, setEditingText] = useState('');
  const [hideSentiments, setHideSentiments] = useState(false);

  const [events, setEvents] = useState<Event[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<number | ''>('');
  const [selectedProduct, setSelectedProduct] = useState<number | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [minRating, setMinRating] = useState<number | ''>('');
  const [maxRating, setMaxRating] = useState<number | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(
    null
  );
  const [selectedSentiment, setSelectedSentiment] = useState('');

  const [geoProducts, setGeoProducts] = useState<GeoProduct[]>([]);
  const [geoPaginationInfo, setGeoPaginationInfo] =
    useState<PaginationInfo | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (user?.employee_permissions?.length) {
      setActiveTab(getInitialTab());
    }
  }, [user?.id]);

  // Never leave the user on a tab they are not allowed to see.
  useEffect(() => {
    if (user && !canSeeTab(activeTab)) setActiveTab(getInitialTab());
  }, [user, activeTab]);

  useEffect(() => {
    if (isAuthenticated && user?.is_employee) {
      const hasPermissions = user?.employee_permissions?.length > 0;
      if (hasPermissions) {
        if (
          activeTab === 'feedbacks' &&
          !user.employee_permissions.includes('Feedbacks')
        ) {
          setActiveTab(getInitialTab());
        }
        if (
          activeTab === 'feedbacks' &&
          user.employee_permissions.includes('Feedbacks')
        ) {
          fetchFeedbacks();
          fetchEmployeeEvents();
          if (location.state) {
            const { defaultEvent, defaultProduct } = location.state as {
              defaultEvent?: number;
              defaultProduct?: number;
            };
            if (defaultEvent) setSelectedEvent(defaultEvent);
            if (defaultProduct) setSelectedProduct(defaultProduct);
          }
        } else if (
          (activeTab === 'access-logs' || activeTab === 'geo') &&
          user.employee_permissions.includes('Geo')
        ) {
          fetchEmployeeEvents();
          if (activeTab === 'geo') fetchGeoProducts();
        }
      } else {
        fetchFeedbacks();
        fetchEmployeeEvents();
        if (location.state) {
          const { defaultEvent, defaultProduct } = location.state as {
            defaultEvent?: number;
            defaultProduct?: number;
          };
          if (defaultEvent) setSelectedEvent(defaultEvent);
          if (defaultProduct) setSelectedProduct(defaultProduct);
        }
      }
    } else if (isAuthenticated && !user?.is_employee) {
      navigate('/');
    }
  }, [isAuthenticated, user, location.state, activeTab]);

  useEffect(() => {
    if (selectedEvent) fetchEventProducts(selectedEvent);
    else setProducts([]);
  }, [selectedEvent]);

  useEffect(() => {
    if (activeTab === 'feedbacks') fetchFeedbacks();
    else if (activeTab === 'geo') fetchGeoProducts();
  }, [
    selectedEvent,
    selectedProduct,
    startDate,
    endDate,
    minRating,
    maxRating,
    currentPage,
    pageSize,
    activeTab,
  ]);

  const fetchEmployeeEvents = async () => {
    try {
      const response = await axios.get(`${backendBaseUrl}/employee/events/`, {
        params: { user_id: user?.id },
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchEventProducts = async (eventId: number) => {
    try {
      const response = await axios.get(
        `${backendBaseUrl}/employee/events/${eventId}/products/`,
        { params: { user_id: user?.id } }
      );
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
    } catch {
      alert('Login failed. Please check your credentials.');
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedEvent) params.append('event_id', selectedEvent.toString());
      if (selectedProduct)
        params.append('product_id', selectedProduct.toString());
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (searchQuery) params.append('q', searchQuery);
      if (minRating) params.append('min_rating', minRating.toString());
      if (maxRating) params.append('max_rating', maxRating.toString());
      if (selectedSentiment) params.append('sentiment', selectedSentiment);
      if (user?.id) params.append('user_id', String(user.id));
      params.append('page', currentPage.toString());
      params.append('page_size', pageSize.toString());
      const response = await axios.get(
        `${backendBaseUrl}/employee/feedbacks/?${params.toString()}`
      );
      setFeedbacks(response.data.feedbacks);
      setPaginationInfo(response.data.pagination);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    }
  };

  const fetchGeoProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (user?.id) params.append('user_id', String(user.id));
      if (selectedEvent) params.append('event_id', selectedEvent.toString());
      params.append('page', currentPage.toString());
      params.append('page_size', pageSize.toString());
      const response = await axios.get(
        `${backendBaseUrl}/employee/geo-products/?${params.toString()}`
      );
      setGeoProducts(response.data.geo_products);
      setGeoPaginationInfo(response.data.pagination);
    } catch (error) {
      console.error('Error fetching geo products:', error);
    }
  };

  const handleResetGeo = async (productId: number, productName: string) => {
    if (
      !window.confirm(
        `Reset geo data for "${productName}"? This will clear position, address and installation date.`
      )
    )
      return;
    try {
      await axios.post(
        `${backendBaseUrl}/employee/products/${productId}/reset-geo/`,
        { user_id: user?.id }
      );
      fetchGeoProducts();
    } catch {
      alert('Failed to reset geo data');
    }
  };

  const handleEditClick = (feedback: Feedback) => {
    setEditingFeedbackId(feedback.id);
    setEditingText(feedback.text);
  };

  const handleEditSubmit = async (feedbackId: string) => {
    try {
      await axios.patch(`${backendBaseUrl}/employee/feedbacks/${feedbackId}/`, {
        text: editingText,
        user_id: user?.id,
      });
      setEditingFeedbackId(null);
      setEditingText('');
      fetchFeedbacks();
    } catch {
      alert('Failed to update feedback');
    }
  };

  const handleDelete = async (feedbackId: string) => {
    try {
      await axios.delete(
        `${backendBaseUrl}/employee/feedbacks/${feedbackId}/delete/`,
        { params: { user_id: user?.id } }
      );
      setConfirmDeleteId(null);
      fetchFeedbacks();
    } catch {
      alert('Failed to delete feedback');
    }
  };

  const resetFilters = () => {
    setSelectedEvent('');
    setSelectedProduct('');
    setProducts([]);
    setStartDate('');
    setEndDate('');
    setMinRating('');
    setMaxRating('');
    setSelectedSentiment('');
    setCurrentPage(1);
  };

  const handleTabChange = (tab: EmpTab) => {
    setActiveTab(tab);
  };

  const getHighlightedText = (text: string, insightsYaml: string) => {
    if (!insightsYaml) return text;
    try {
      const insights = yaml.load(insightsYaml) as any;
      let highlightedText = text;
      if (insights?.reviews?.[0]?.themes) {
        const allPortions = insights.reviews[0].themes
          .flatMap((theme: any) =>
            theme.portions.map((p: any) => ({
              ...p,
              theme: theme.theme,
              generic_area: theme.generic_area,
            }))
          )
          .sort(
            (a: any, b: any) =>
              text.indexOf(b.portion) - text.indexOf(a.portion)
          );
        allPortions.forEach((portion: any) => {
          const color =
            portion.sentiment === 'Positive'
              ? '#00d27a'
              : portion.sentiment === 'Negative'
                ? '#e63757'
                : '#9da9bb';
          highlightedText = highlightedText.replace(
            portion.portion,
            `<span style="background:${color}20;border-bottom:2px solid ${color};padding:0 2px;border-radius:2px;" title="${portion.theme} (${portion.generic_area})">${portion.portion}</span>`
          );
        });
      }
      return <div dangerouslySetInnerHTML={{ __html: highlightedText }} />;
    } catch {
      return text;
    }
  };

  const sentimentTagClass = (sentiment: string) => {
    const s = sentiment.toLowerCase();
    if (s === 'positive') return 'mgmt-badge mgmt-badge--success';
    if (s === 'negative') return 'mgmt-badge mgmt-badge--danger';
    return 'mgmt-badge mgmt-badge--secondary';
  };

  /* ── Login screen ──────────────────────────────────────────────────────── */
  if (!isAuthenticated) {
    return (
      <div className="mgmt-login-page">
        <div className="mgmt-login-card">
          <div className="mgmt-login-card__logo">
            <span style={{ fontSize: '2rem' }}>📋</span>
          </div>
          <h2 className="mgmt-login-card__title">Employee Dashboard</h2>
          <p className="mgmt-login-card__sub">
            Sign in with your credentials to continue
          </p>
          <form onSubmit={handleLogin}>
            <div className="mgmt-form-group">
              <label className="mgmt-form-label">Username or Email</label>
              <input
                type="text"
                className="mgmt-form-control"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="mgmt-form-group">
              <label className="mgmt-form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="mgmt-form-control"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={
                    showPassword ? 'Nascondi password' : 'Mostra password'
                  }
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="mgmt-btn mgmt-btn--primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
              }}
            >
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!user?.is_employee) {
    return (
      <div className="mgmt-login-page">
        <div className="mgmt-error" style={{ maxWidth: 400 }}>
          Access denied. Only employees can view this dashboard.
        </div>
      </div>
    );
  }

  /* ── Authenticated layout ──────────────────────────────────────────────── */
  return (
    <div className="mgmt-page">
      <EmployeeNavbar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        userPermissions={user?.employee_permissions || []}
      />

      <div className="mgmt-body">
        {/* ── Feedbacks tab ─────────────────────────────────────────────── */}
        {activeTab === 'feedbacks' && canSeeTab('feedbacks') && (
          <>
            <h2 className="mgmt-page-title">Feedback</h2>

            {/* Filters */}
            <div className="mgmt-filters">
              <div className="mgmt-filters__search">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="mgmt-filters__search-input"
                  placeholder="Search in feedback body…"
                  onKeyDown={e => e.key === 'Enter' && fetchFeedbacks()}
                />
                <button
                  className="mgmt-filters__search-btn"
                  onClick={fetchFeedbacks}
                >
                  🔍
                </button>
              </div>

              <div className="mgmt-filter-group">
                <label>Event</label>
                <select
                  value={selectedEvent}
                  onChange={e =>
                    setSelectedEvent(
                      e.target.value ? Number(e.target.value) : ''
                    )
                  }
                  className="mgmt-select"
                >
                  <option value="">All Events</option>
                  {events.map(ev => (
                    <option key={ev.id} value={ev.id}>
                      {ev.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mgmt-filter-group">
                <label>Product</label>
                <select
                  value={selectedProduct}
                  onChange={e =>
                    setSelectedProduct(
                      e.target.value ? Number(e.target.value) : ''
                    )
                  }
                  className="mgmt-select"
                  disabled={!selectedEvent}
                >
                  <option value="">All Products</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mgmt-filter-group">
                <label>Date Range</label>
                <div className="mgmt-date-range">
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="mgmt-date-input"
                    max={endDate || undefined}
                  />
                  <span className="mgmt-date-sep">→</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="mgmt-date-input"
                    min={startDate || undefined}
                  />
                </div>
              </div>

              <div className="mgmt-filter-group">
                <label>Rating</label>
                <div className="mgmt-rating-range">
                  <select
                    value={minRating}
                    onChange={e =>
                      setMinRating(e.target.value ? Number(e.target.value) : '')
                    }
                    className="mgmt-select"
                    style={{ minWidth: 90 }}
                  >
                    <option value="">Min ★</option>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => (
                      <option key={`min-${r}`} value={r}>
                        {r} ★
                      </option>
                    ))}
                  </select>
                  <span className="mgmt-date-sep">–</span>
                  <select
                    value={maxRating}
                    onChange={e =>
                      setMaxRating(e.target.value ? Number(e.target.value) : '')
                    }
                    className="mgmt-select"
                    style={{ minWidth: 90 }}
                  >
                    <option value="">Max ★</option>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => (
                      <option key={`max-${r}`} value={r}>
                        {r} ★
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mgmt-filter-group">
                <label>Sentiment</label>
                <select
                  value={selectedSentiment}
                  onChange={e => setSelectedSentiment(e.target.value)}
                  className="mgmt-select"
                >
                  <option value="">All</option>
                  <option value="Positive">Positive</option>
                  <option value="Negative">Negative</option>
                  <option value="Neutral">Neutral</option>
                </select>
              </div>

              <div className="mgmt-filters__footer">
                <div className="mgmt-filters__footer-left">
                  <div className="mgmt-page-size">
                    <label>Per page</label>
                    <select
                      value={pageSize}
                      onChange={e => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[10, 20, 30, 50].map(n => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={resetFilters}
                  className="mgmt-btn mgmt-btn--reset mgmt-btn--sm"
                >
                  🧹 Reset filters
                </button>
              </div>
            </div>

            {/* Feedback list */}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {feedbacks.map(fb => (
                <li key={fb.id} className="mgmt-feedback-item">
                  <div className="mgmt-feedback-item__header">
                    <h5 className="mgmt-feedback-item__title">
                      {fb.title || 'Feedback'}
                      <span className="mgmt-feedback-item__stars">
                        {[1, 2, 3, 4, 5].map(s => (
                          <FaStar
                            key={s}
                            size={16}
                            color={fb.rating >= s ? '#f5803e' : '#d8e2ef'}
                          />
                        ))}
                      </span>
                    </h5>
                    <div className="mgmt-feedback-item__actions">
                      {editingFeedbackId === fb.id ? (
                        <button
                          onClick={() => handleEditSubmit(fb.id)}
                          className="mgmt-btn mgmt-btn--primary mgmt-btn--sm"
                        >
                          <FaSave size={11} /> Save
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditClick(fb)}
                          className="mgmt-btn mgmt-btn--outline mgmt-btn--sm"
                        >
                          <FaPencilAlt size={11} /> Edit
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDeleteId(fb.id)}
                        className="mgmt-btn mgmt-btn--danger mgmt-btn--sm"
                      >
                        <FaTimes size={11} />
                      </button>
                    </div>
                  </div>

                  <div className="mgmt-feedback-item__meta">
                    <span className="mgmt-feedback-item__meta-item">
                      🕐{' '}
                      {new Date(fb.created_at).toLocaleString('it-IT', {
                        timeZone: 'Europe/Rome',
                      })}
                    </span>
                    {fb.event_name && (
                      <span className="mgmt-feedback-item__meta-item">
                        📅 {fb.event_name}
                      </span>
                    )}
                    {fb.product_name && (
                      <span className="mgmt-feedback-item__meta-item">
                        📦 {fb.product_name}
                      </span>
                    )}
                    {fb.sentiment && (
                      <span className={sentimentTagClass(fb.sentiment)}>
                        {fb.sentiment}
                      </span>
                    )}
                  </div>

                  <div className="mgmt-feedback-item__body">
                    <div className="mgmt-feedback-item__text">
                      {editingFeedbackId === fb.id ? (
                        <textarea
                          value={editingText}
                          onChange={e => setEditingText(e.target.value)}
                          className="mgmt-feedback-item__textarea"
                          rows={4}
                        />
                      ) : !hideSentiments ? (
                        getHighlightedText(fb.text, fb.insights)
                      ) : (
                        <span>{fb.text}</span>
                      )}

                      {fb.tags && fb.tags.length > 0 && (
                        <div className="mgmt-feedback-item__tags">
                          {fb.tags.map(([tag, sentiment], i) => (
                            <span
                              key={i}
                              className={sentimentTagClass(sentiment)}
                              title={sentiment}
                            >
                              {tag.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      )}

                      <button
                        className="mgmt-perception-toggle"
                        onClick={() => setHideSentiments(!hideSentiments)}
                      >
                        {hideSentiments ? 'Show perception' : 'Hide perception'}
                      </button>

                      {fb.type !== 'text' && (
                        <audio
                          controls
                          src={`/audio_files/${fb.token}/${fb.filename}`}
                          className="mgmt-feedback-item__audio"
                        />
                      )}
                    </div>

                    {fb.product_image && (
                      <div className="mgmt-feedback-item__image">
                        <img
                          src={resolveMediaUrl(fb.product_image)}
                          alt={fb.product_name || 'Product'}
                        />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {feedbacks.length === 0 && (
              <div className="mgmt-empty">
                <span className="mgmt-empty__icon">💬</span>
                <span className="mgmt-empty__text">
                  No feedback found for the current filters.
                </span>
              </div>
            )}

            {/* Pagination */}
            {paginationInfo && (
              <div className="mgmt-pagination">
                <span className="mgmt-pagination__count">
                  {paginationInfo.total_count} item
                  {paginationInfo.total_count !== 1 ? 's' : ''}
                </span>
                <div className="mgmt-pagination__pages">
                  <button
                    className="mgmt-pagination__btn"
                    onClick={() => setCurrentPage(p => p - 1)}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft size={11} /> Prev
                  </button>
                  <span className="mgmt-pagination__page-label">
                    {currentPage} / {paginationInfo.total_pages}
                  </span>
                  <button
                    className="mgmt-pagination__btn"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage === paginationInfo.total_pages}
                  >
                    Next <FaChevronRight size={11} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Products tab ──────────────────────────────────────────────── */}
        {activeTab === 'products' && canSeeTab('products') && <ProductsTab />}

        {activeTab === 'market-test' && canSeeTab('market-test') && (
          <MarketTestTab />
        )}

        {/* ── Access logs tab ───────────────────────────────────────────── */}
        {activeTab === 'access-logs' && canSeeTab('access-logs') && (
          <AccessLogs />
        )}

        {/* ── Geo tab ───────────────────────────────────────────────────── */}
        {activeTab === 'geo' && canSeeTab('geo') && (
          <>
            <h2 className="mgmt-page-title">Registered Products</h2>

            <div className="mgmt-filters">
              <div className="mgmt-filters__search">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="mgmt-filters__search-input"
                  placeholder="Search by product or serial…"
                  onKeyDown={e => e.key === 'Enter' && fetchGeoProducts()}
                />
                <button
                  className="mgmt-filters__search-btn"
                  onClick={fetchGeoProducts}
                >
                  🔍
                </button>
              </div>

              <div className="mgmt-filter-group">
                <label>Event</label>
                <select
                  value={selectedEvent}
                  onChange={e =>
                    setSelectedEvent(
                      e.target.value ? Number(e.target.value) : ''
                    )
                  }
                  className="mgmt-select"
                >
                  <option value="">All Events</option>
                  {events.map(ev => (
                    <option key={ev.id} value={ev.id}>
                      {ev.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mgmt-filter-group">
                <label>Installation Date</label>
                <div className="mgmt-date-range">
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="mgmt-date-input"
                  />
                  <span className="mgmt-date-sep">→</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="mgmt-date-input"
                  />
                </div>
              </div>

              <div className="mgmt-filters__footer">
                <div className="mgmt-filters__footer-left">
                  <div className="mgmt-page-size">
                    <label>Per page</label>
                    <select
                      value={pageSize}
                      onChange={e => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[10, 20, 30, 50].map(n => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={resetFilters}
                  className="mgmt-btn mgmt-btn--reset mgmt-btn--sm"
                >
                  🧹 Reset filters
                </button>
              </div>
            </div>

            <div className="mgmt-table-wrap">
              <table className="mgmt-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Serial Number</th>
                    <th>Registered Location</th>
                    <th>Registration Date</th>
                    <th>Event</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {geoProducts.map(gp => (
                    <tr key={gp.id}>
                      <td>{gp.product_name}</td>
                      <td>
                        <span className="cell-mono">{gp.serial_number}</span>
                      </td>
                      <td
                        style={{
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          color: 'var(--mgmt-gray-600)',
                        }}
                      >
                        {gp.registered_location}
                      </td>
                      <td>{gp.registration_date}</td>
                      <td>
                        {gp.event_name ? (
                          <span className="mgmt-badge mgmt-badge--primary">
                            {gp.event_name}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--mgmt-gray-400)' }}>
                            —
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="mgmt-btn mgmt-btn--outline mgmt-btn--sm"
                          onClick={() => handleResetGeo(gp.id, gp.product_name)}
                        >
                          Reset
                        </button>
                      </td>
                    </tr>
                  ))}
                  {geoProducts.length === 0 && (
                    <tr>
                      <td colSpan={6} className="mgmt-table__empty">
                        No registered products found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {geoPaginationInfo && (
              <div className="mgmt-pagination">
                <span className="mgmt-pagination__count">
                  {geoPaginationInfo.total_count} item
                  {geoPaginationInfo.total_count !== 1 ? 's' : ''}
                </span>
                <div className="mgmt-pagination__pages">
                  <button
                    className="mgmt-pagination__btn"
                    onClick={() => setCurrentPage(p => p - 1)}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft size={11} /> Prev
                  </button>
                  <span className="mgmt-pagination__page-label">
                    {currentPage} / {geoPaginationInfo.total_pages}
                  </span>
                  <button
                    className="mgmt-pagination__btn"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage === geoPaginationInfo.total_pages}
                  >
                    Next <FaChevronRight size={11} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {confirmDeleteId && (
        <div className="mgmt-modal-backdrop" onClick={() => setConfirmDeleteId(null)}>
          <div className="mgmt-modal mgmt-modal--confirm" onClick={e => e.stopPropagation()}>
            <h4 className="mgmt-modal__title">Delete feedback</h4>
            <p className="mgmt-modal__body">
              Are you sure you want to delete this feedback? This action cannot
              be undone.
            </p>
            <div className="mgmt-modal__footer">
              <button
                className="mgmt-btn mgmt-btn--outline mgmt-btn--sm"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="mgmt-btn mgmt-btn--danger mgmt-btn--sm"
                onClick={() => handleDelete(confirmDeleteId)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedbacks;
